import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface NewVersionModalProps {
  open: boolean;
  onClose: () => void;
  currentVersion: string;
  onCreateVersion: (version: string, versionNotes: string, isMajor: boolean) => void;
  isCreating: boolean;
}

export function NewVersionModal({
  open,
  onClose,
  currentVersion,
  onCreateVersion,
  isCreating,
}: NewVersionModalProps) {
  const [versionType, setVersionType] = useState<'minor' | 'major'>('minor');
  const [versionNotes, setVersionNotes] = useState('');

  const getNextVersion = () => {
    const parts = currentVersion.split('.');
    if (versionType === 'major') {
      return `${parseInt(parts[0]) + 1}.0`;
    } else {
      return `${parts[0]}.${parseInt(parts[1] || '0') + 1}`;
    }
  };

  const handleCreate = () => {
    const newVersion = getNextVersion();
    onCreateVersion(newVersion, versionNotes, versionType === 'major');
    setVersionNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nieuwe Versie Aanmaken</DialogTitle>
          <DialogDescription>
            Maak een nieuwe versie van de handleiding. Alle secties worden gekopieerd naar de
            nieuwe versie.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Huidige versie: v{currentVersion}</Label>
            <RadioGroup value={versionType} onValueChange={(v) => setVersionType(v as any)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="minor" id="minor" />
                <Label htmlFor="minor" className="font-normal">
                  Minor update (v{getNextVersion()}) - Kleine wijzigingen, fixes
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="major" id="major" />
                <Label htmlFor="major" className="font-normal">
                  Major update (v{getNextVersion()}) - Grote wijzigingen, nieuwe features
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Versie Notities</Label>
            <Textarea
              id="notes"
              value={versionNotes}
              onChange={(e) => setVersionNotes(e.target.value)}
              placeholder="Beschrijf wat er is gewijzigd in deze versie..."
              className="min-h-[100px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="outline" disabled={isCreating}>
            Annuleren
          </Button>
          <Button onClick={handleCreate} disabled={isCreating || !versionNotes.trim()}>
            Nieuwe Versie Aanmaken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
